import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const ProxiesTable = ({ proxies, loading }) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLocationString = (info) => {
    if (!info) return '';
    const parts = [];
    if (info.city) parts.push(info.city);
    if (info.country) parts.push(info.country);
    return parts.join(', ');
  };

  const getISPString = (info) => {
    if (!info || !info.org) return '';
    return info.org;
  };

  const getTotalBookiesCount = (bookies) => {
    if (!bookies || typeof bookies !== 'object') return 0;
    return Object.keys(bookies).length;
  };

  const getUnhealthyCount24h = (healthHistory) => {
    if (!healthHistory || !Array.isArray(healthHistory)) return 0;
    
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
    
    return healthHistory.filter(entry => {
      if (!entry || entry.status !== 'unhealthy') return false;
      
      const entryTime = new Date(entry.timestamp);
      return entryTime >= twentyFourHoursAgo;
    }).length;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return sortConfig.direction === 'asc' 
      ? <span className="ml-1 text-[#E8FC8B]">↑</span>
      : <span className="ml-1 text-[#E8FC8B]">↓</span>;
  };

  const sortedProxies = useMemo(() => {
    if (!proxies || !sortConfig.key) return proxies;

    const sorted = [...proxies].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'serverName': {
          aValue = a.serverName || '';
          bValue = b.serverName || '';
          
          // Natural sort for server names (handles vm1, vm2, vm10 correctly)
          const naturalSort = (str1, str2) => {
            return str1.localeCompare(str2, undefined, {
              numeric: true,
              sensitivity: 'base'
            });
          };
          
          const result = naturalSort(aValue, bValue);
          return sortConfig.direction === 'asc' ? result : -result;
        }
          
        case 'active':
          aValue = a.active ? 1 : 0;
          bValue = b.active ? 1 : 0;
          break;
        case 'addedOn':
          aValue = new Date(a.addedOn || 0);
          bValue = new Date(b.addedOn || 0);
          break;
        case 'updatedOn':
          aValue = new Date(a.updatedOn || 0);
          bValue = new Date(b.updatedOn || 0);
          break;
        default:
          return 0;
      }

      // For non-serverName columns, use regular comparison
      if (sortConfig.key !== 'serverName') {
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
    });

    return sorted;
  }, [proxies, sortConfig]);

  // Calculate proxy statistics
  const proxyStats = useMemo(() => {
    if (!proxies || proxies.length === 0) {
      return { active: 0, handlers: 0, inactive: 0, total: 0, activeByCountry: {} };
    }

    const active = proxies.filter(p => p.active).length;
    const handlers = proxies.filter(p => p.active && p.handler).length;
    const inactive = proxies.filter(p => !p.active).length;
    const total = proxies.length;

    // Calculate active proxies by country
    const activeByCountry = proxies
      .filter(p => p.active)
      .reduce((acc, proxy) => {
        const country = proxy.country || 'Unknown';
        acc[country] = (acc[country] || 0) + 1;
        return acc;
      }, {});

    return { active, handlers, inactive, total, activeByCountry };
  }, [proxies]);

  const renderSortableHeader = (key, label) => {
    const sortableColumns = ['serverName', 'active', 'addedOn', 'updatedOn'];
    const isSortable = sortableColumns.includes(key);
    
    if (isSortable) {
      return (
        <button
          onClick={() => handleSort(key)}
          className="flex items-center text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider hover:text-[#E8FC8B] transition-colors duration-200"
        >
          {label}
          {getSortIcon(key)}
        </button>
      );
    }
    
    return label;
  };

  if (loading) {
    return (
      <div className="text-center text-[#ABCDDF] py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E8FC8B] mx-auto mb-2"></div>
        {t('loading')}...
      </div>
    );
  }

  if (!proxies || proxies.length === 0) {
    return (
      <div className="text-center text-[#ABCDDF] py-8">
        {t('noProxiesAvailable')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      {/* Proxy Statistics */}
      <div className="mb-6 bg-[#2A3C29] rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#E8FC8B]">{proxyStats.total}</div>
            <div className="text-sm text-[#ABCDDF]">Total Proxies</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-4">
              <div>
                <div className="text-2xl font-bold text-green-400">{proxyStats.active}</div>
                <div className="text-sm text-[#ABCDDF]">Active Proxies</div>
              </div>
              {Object.keys(proxyStats.activeByCountry).length > 0 && (
                <div className="text-xs text-[#ABCDDF] border-l border-[#3A4C39] pl-4">
                  {Object.entries(proxyStats.activeByCountry)
                    .sort(([,a], [,b]) => b - a) // Sort by count descending
                    .map(([country, count]) => (
                      <div key={country} className="flex items-center gap-1 mb-1 last:mb-0">
                        <span className="text-green-300">{country}:</span>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">{proxyStats.handlers}</div>
            <div className="text-sm text-[#ABCDDF]">Handler Actives</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-400">{proxyStats.inactive}</div>
            <div className="text-sm text-[#ABCDDF]">Inactive Proxies</div>
          </div>
        </div>
      </div>

      <table className="w-full bg-[#2A3C29] rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-[#1B2D1A]">
            <th className="px-4 py-3 text-left">
              {renderSortableHeader('serverName', t('proxyServer'))}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              {t('proxyId')}
            </th>
            <th className="px-4 py-3 text-left">
              {renderSortableHeader('active', t('proxyActive'))}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              {t('proxyBookies')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              {t('proxyActiveBookies')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              {t('proxyAddress')}
            </th>
            <th className="px-4 py-3 text-left">
              {renderSortableHeader('addedOn', t('proxyAddedOn'))}
            </th>
            <th className="px-4 py-3 text-left">
              {renderSortableHeader('updatedOn', t('proxyUpdatedOn'))}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              IMEI
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              Unhealthy 24h
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              {t('proxyISP')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-[#ABCDDF] uppercase tracking-wider">
              {t('proxyLocation')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#3A4C39]">
          {sortedProxies.map((proxy, index) => (
            <tr key={proxy._id?.hostname || proxy.hostname || index} className="hover:bg-[#3A4C39] transition-colors duration-150">
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {proxy.serverName || 'N/A'}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-white">
                {proxy._id?.hostname || proxy.hostname || 'N/A'}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {proxy.active ? (
                  <span className="text-[#E8FC8B]">
                    ✓{proxy.handler && ' (Handler)'}
                  </span>
                ) : (
                  <span className="text-gray-400">✗</span>
                )}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {getTotalBookiesCount(proxy.bookies)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {proxy.currentActiveBookies || 0}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {proxy.proxy?.externalHostname || proxy.externalHostname || 'N/A'}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {formatDate(proxy.addedOn)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {formatDate(proxy.updatedOn)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {proxy.imei || 'N/A'}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  getUnhealthyCount24h(proxy.healthHistory) > 0 
                    ? 'bg-red-500 text-white' 
                    : 'bg-green-500 text-white'
                }`}>
                  {getUnhealthyCount24h(proxy.healthHistory)}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white max-w-xs">
                <div className="truncate" title={getISPString(proxy.info)}>
                  {getISPString(proxy.info)}
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                {getLocationString(proxy.info)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProxiesTable; 